import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APIResponse, Client, Contract, Data, StrapiFile, User } from '@mlc/types';
import { environment } from 'src/environments/environment';
import { DocumentService } from '../document/document.service';
import { EMPTY, forkJoin, map, Observable, switchMap, take } from 'rxjs';
import { Documents } from '../../models/documents.model';
import { DocumentsEnum } from '../../models/enums/typeDocuments.enum';
import { Request } from '../../models/request.model';
import { RequestPayload } from '../../models/requestPayload.model';
import { RequestTypeEnum } from '../../models/enums/requestType.enum';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { UserService } from '../user/user.service';
import { UserDetailsFormModel } from '../../models/userDetailsFormModel.model';
import { RequestFormModel } from '../../models/dataRequest.model';

@Injectable({
  providedIn: 'root'
})
export class RequestService {

  constructor(
    private httpClient: HttpClient, 
    private documentService: DocumentService,
    private userService: UserService
  ) { }

  processRequest( data: RequestFormModel, contract: Data<Contract>, formlyConfig: FormlyFieldConfig[], requestType: RequestTypeEnum): Observable<Request> {
 
    const files: File[] = this.extractFilesFromForm(data, formlyConfig);

    return this.uploadFiles(files).pipe(
      switchMap((uploaded: StrapiFile[]) => {
        const fileUrls: string[] = uploaded.map(file => file.url);

        data.fileUrls = fileUrls;

        return this.createDocumentsForFiles(uploaded, contract.id).pipe(
          map(documentIds => ({ documentIds }))
        );
      }),

      switchMap(({ documentIds }) =>
        this.updateUserIfNeeded(data, contract).pipe(
          map(() => ({ documentIds }))
        )
      ),

      switchMap(({ documentIds }) => {
        const payload: { data: RequestPayload } = {
          data: {
            type: requestType,
            documents: documentIds,
            user: contract.attributes.user.data.id,
            userProductWallet: contract.id,
            data,
            formlyConfig,
          },
        };

        return this.createRequest(payload);
      })
    );
  }

  private extractFilesFromForm(data: RequestFormModel, formlyConfig: FormlyFieldConfig[]): File[] {
    const files: File[] = [];

    formlyConfig.forEach(section => {
      section.fieldGroup?.forEach(field => {
        if (field.type === 'ion-file' && field.key) {
          const key = field.key as keyof RequestFormModel;
          const file: File = data[key] as File;
          if (file) {
            files.push(file);
          }
        }
      });
    });

    return files;
  }

  private uploadFiles(files: File[]): Observable<StrapiFile[]> {
    if (files.length === 0) return EMPTY;

    const formData: FormData = new FormData();
    files.forEach(file => formData.append('files', file));

    return this.documentService.uploadFile(formData);
  }

  private createDocumentsForFiles(files: StrapiFile[], contractId: number): Observable<number[]> {
    const requests$: Observable<APIResponse<Data<Documents>>>[] = files.map(f =>
      this.documentService.createDocument(DocumentsEnum.CONTRACT_DOCUMENT, f.id!, contractId)
    );

    return forkJoin(requests$).pipe(
      map(responses => responses.map(response => response.data.id))
    );
  }

  private updateUserIfNeeded(data: RequestFormModel, contract: Data<Contract>): Observable<Client> {
    const currentUserAttrs: User = contract.attributes.user.data.attributes;
    const formUpdateUser: Partial<UserDetailsFormModel> = {};
    let hasChanges: boolean = false;

    for (const key of Object.keys(data) as (keyof UserDetailsFormModel)[]) {
      const newValue = (data as any)[key];
      const oldValue = (currentUserAttrs as any)[key];
      if (newValue !== undefined && newValue !== oldValue) {
        formUpdateUser[key] = newValue;
        hasChanges = true;
      }
    }

    return hasChanges
      ? this.userService.editInformationUser(formUpdateUser, contract.attributes.user.data.id)
      : EMPTY;
  }

  createRequest(request: { data: RequestPayload }): Observable<Request> {
    return this.httpClient
      .post<APIResponse<Data<Request>>>(`${environment.baseUrl}/contracts/${request.data.userProductWallet}/requests`, request)
      .pipe(
        take(1),
        map((response: APIResponse<Data<Request>>) => response.data.attributes)
      );
  }

  findAllRequest(userProductWalletId: number): Observable<Data<Request>[]> {
    return this.httpClient.get<APIResponse<Data<Request>[]>>(`${environment.baseUrl}/contracts/${userProductWalletId}/requests`)
    .pipe(
      take(1),
      map((response: APIResponse<Data<Request>[]>) => response.data)
    )
  }

  findOneRequest(userProductWalletId: number, requestId: number): Observable<Data<Request>> {
    return this.httpClient.get<APIResponse<Data<Request>>>(`${environment.baseUrl}/contracts/${userProductWalletId}/requests/${requestId}`)
    .pipe(
      take(1),
      map((response: APIResponse<Data<Request>>) => response.data)
    )
  }
}
